import { Phone, Mail, Globe, MapPin, Clock } from "lucide-react";
import QuoteForm from "../components/site/QuoteForm";
import useSEO from "../hooks/useSEO";

export default function Contact() {
  useSEO({
    title: "Contact Fire Safety Systems | Ireland",
    description:
      "Contact Fire Safety Systems for a quotation on fire alarm systems, emergency lighting, fire extinguishers, testing and maintenance. Nationwide service across Ireland.",
    canonicalPath: "/contact",
  });

  return (
    <>
      <section className="bg-white border-b border-slate-200">
        <div className="fss-container py-16 md:py-20">
          <div className="max-w-3xl">
            <div className="fss-overline">Get in touch</div>
            <h1 className="mt-4 fss-headline text-4xl sm:text-5xl lg:text-6xl text-slate-900">
              Speak to our team about your<br />
              <span className="text-green-700">fire safety</span> requirements.
            </h1>
            <p className="mt-6 text-lg text-slate-600 leading-relaxed">
              Whether you need a new system installation, a maintenance contract,
              or simply expert advice, our team is ready to help.
            </p>
          </div>
        </div>
      </section>

      <section className="fss-section bg-slate-50 border-b border-slate-200">
        <div className="fss-container grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* Contact details */}
          <aside className="lg:col-span-4 space-y-6">
            <div className="fss-card">
              <div className="fss-overline">Contact details</div>
              <div className="mt-4">
                <div className="font-display text-lg font-semibold text-slate-900">
                  Fire Safety Systems
                </div>
                <div className="text-sm text-slate-500 mt-1">
                  Fire safety installation, testing &amp; maintenance
                </div>
              </div>
              <ul className="mt-5 space-y-5">
                <ContactRow
                  icon={Phone}
                  label="Telephone"
                  value="01 835 1454"
                  href="tel:018351454"
                  testId="contact-phone"
                />
                <ContactRow
                  icon={Mail}
                  label="Email"
                  value="service@fssl.ie"
                  href="mailto:service@fssl.ie"
                  testId="contact-email"
                />
                <ContactRow
                  icon={Globe}
                  label="Website"
                  value="www.fssl.ie"
                  testId="contact-website"
                />
                <ContactRow
                  icon={MapPin}
                  label="Coverage"
                  value="Nationwide · Ireland"
                  testId="contact-coverage"
                />
                <ContactRow
                  icon={Clock}
                  label="Business Hours"
                  value="Mon–Fri · 9am–5pm"
                  testId="contact-hours"
                />
              </ul>
            </div>

            <div className="bg-slate-900 text-white p-8 rounded-sm relative overflow-hidden">
              <div className="fss-ribbon absolute top-0 inset-x-0" />
              <div className="text-xs font-bold tracking-[0.22em] uppercase text-red-500 mb-3">
                Prefer to talk?
              </div>
              <h3 className="font-display text-xl font-semibold mb-3">
                Speak directly with our team
              </h3>
              <p className="text-sm text-slate-300 leading-relaxed mb-5">
                For urgent enquiries or if you would rather discuss your
                requirements over the phone, call our line directly.
              </p>
              <a
                href="tel:018351454"
                className="fss-btn fss-btn-accent w-full justify-center"
                data-testid="contact-urgent-call"
              >
                <Phone className="w-4 h-4 mr-2" />
                01 835 1454
              </a>
            </div>
          </aside>

          {/* Form */}
          <div className="lg:col-span-8">
            <div className="mb-6">
              <div className="fss-overline">Request a Quote or Consultation</div>
              <h2 className="mt-3 font-display text-2xl md:text-3xl font-semibold text-slate-900">
                Tell us about your premises
              </h2>
              <p className="mt-2 text-slate-600 max-w-2xl">
                Complete the form below and a member of our team will be in touch.
              </p>
            </div>
            <QuoteForm />
          </div>
        </div>
      </section>

      <section className="bg-white">
        <div className="fss-container py-12 text-center">
          <p className="font-display text-lg text-slate-700 italic">
            &ldquo;Fire Safety Systems You Can Rely On.&rdquo;
          </p>
        </div>
      </section>
    </>
  );
}

function ContactRow({ icon: Icon, label, value, href, testId }) {
  const content = (
    <div className="flex items-start gap-4">
      <div className="w-10 h-10 bg-red-50 border border-red-100 flex items-center justify-center shrink-0 rounded-sm">
        <Icon className="w-5 h-5 text-red-600" />
      </div>
      <div>
        <div className="text-xs font-bold tracking-[0.18em] uppercase text-slate-500">
          {label}
        </div>
        <div className="mt-1 font-display font-semibold text-slate-900">{value}</div>
      </div>
    </div>
  );
  return (
    <li data-testid={testId}>
      {href ? (
        <a href={href} className="block hover:opacity-80 transition-opacity">
          {content}
        </a>
      ) : (
        content
      )}
    </li>
  );
}
